import { useEffect, useState } from 'react';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import Grid from '@mui/material/Grid';
import { useStyles } from "../../common/appStyles";
import { Fab, FormControl, List, ListItem, Paper, Skeleton, ThemeProvider, Tooltip, createTheme } from '@mui/material';
import { showSnackbar } from '../../services/snackbar.service';
import { useSelector } from 'react-redux';
import { RootState } from '../../store/store';
import { PageEnum, Predicates } from '../../types/enums';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import { PageHeader } from "../../components/PageHeader";
import { LoadingDialog } from '../../components/HelperComponents/LoadingDialog';
import { GetSponsoredAds, UploadSponsoredAd } from '../../services/user-service';
import { DatePicker } from '@mui/x-date-pickers';
import { RedirectToDash } from '../RedirectToDash';
import { ISponsoredAdInformationUpper, ISponsoredAdsUpper } from '../../types/manage';
import EditIcon from '@mui/icons-material/Edit';
import AddIcon from '@mui/icons-material/Add';
import { SponsoredAdDialog } from './SponsoredAdDialog';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';
import { IRequestedExpressions } from '../../types/expression';
import { DownloadViewCountReport } from '../../services/user-service';
import { Protoexpression } from '../../utilities/ProtoExpression';
import { MAX_NUMBER_OF_ADS } from '../../components/main-layout/consts';
/**
 * @returns - a page for getting/setting ads and getting ad view data.
 * @remarks The Manage Sponsored Ads page lets MSA users view and change the current ads circulating
 * through the screens on the application. It also allows for users to get an excel file of all Ads and the views
 * they got for a selected month and year.
 */
export function ManageSponsoredAds() {
    const userRole = useSelector((state: RootState) => state.roles);
    const currentPage = userRole.userPageAccess[PageEnum.DownloadUserData];
    const classes = useStyles();
    const [startDateTime, setStartDateTime] = useState<Date>(dayjs(new Date()).date(1).toDate());
    const [endDateTime, setEndDateTime] = useState<Date>(dayjs(new Date()).date(1).toDate());
    const difference = dayjs(endDateTime).diff(dayjs(startDateTime), 'hours');
    const [isLoadingAds, setIsLoadingAds] = useState(false);

    const [isLoading, setIsLoading] = useState(false);
    const [isDisabled, setDisabled] = useState(true);
    const [showDialog, setShowDialog] = useState(false);
    const [sponsoredAds, setSponsoredAds] = useState<ISponsoredAdsUpper[]>([]);
    const [selectedAd, setSelectedAd] = useState<ISponsoredAdInformationUpper>();
    const [pageText, setPageText] = useState<string>("");
    const [selectedAdFile, setSelectedAdFile] = useState(null);

    const theme = createTheme({
        palette: {
            primary: {
                main: '#10AD61'
            },
            secondary: {
                main: '#10AD61'
            },
            background: {
                default: '#ffffff'
            },
        },
        typography: {
            h6: {
                color: '#484A57'
            },
            body1: {
                color: 'black'
            },
            subtitle1: {
                color: '#21C452',
                textDecoration: 'none',
                '&:visited': {
                    color: '#06bd9b',
                },
            }
        },
    });

    const base64ToBlob = (base64: string): Blob => {
        const byteCharacters = atob(base64);
        const byteArrays: number[] = [];
        for (let i = 0; i < byteCharacters.length; i++) {
            byteArrays.push(byteCharacters.charCodeAt(i));
        }
        return new Blob([new Uint8Array(byteArrays)], { type: 'image/png' });
    };

    const getAds = async () => {
        setIsLoadingAds(true)
        try {
            const response = await GetSponsoredAds();
            setSponsoredAds(response);
        } catch (error) {
            showSnackbar(error, 'error', 5000);
        }
        finally {
            setIsLoadingAds(false);
        }
    }

    const handleStartDate = (date) => {
        if (date !== null) {
            const formattedDate = dayjs(date.$d).date(1);
            setStartDateTime(formattedDate.toDate());
        }
    }

    const handleEndDate = (date) => {
        if (date !== null) {
            const formattedDate = dayjs(date.$d).date(1);
            setEndDateTime(formattedDate.toDate());
        }
    }

    const getViewCountReport = async (expression: IRequestedExpressions) => {
        try {
            const viewCountReportBlob = await DownloadViewCountReport(expression);
            const blob = new Blob([viewCountReportBlob], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
            const url = URL.createObjectURL(blob);
            const anchor = document.createElement('a');
            anchor.href = url;
            const start: string = (startDateTime.getMonth() + 1) + " " + startDateTime.getFullYear();
            const end: string = (endDateTime.getMonth() + 1) + " " + endDateTime.getFullYear();
            anchor.download = `ViewCountData ${start} to ${end}.xlsx`;
            anchor.click();
            URL.revokeObjectURL(url);
        } catch (error) {
            throw error;
        } finally {
            setIsLoading(false);
        }
    }

    const handleSubmit = (event) => {
        setIsLoading(true);
        if (difference < 0) {
            showSnackbar("invalid Date Range", "warning", 5000)
            setIsLoading(false);
        }
        else {
            getViewCountReport({
                protoexpressions: [
                    Protoexpression("CreateDateTime", startDateTime.toDateString(), Predicates.GreaterThanEquals),
                    Protoexpression("CreateDateTime", endDateTime.toDateString(), Predicates.LessThanEquals)
                ]
            })
        }
        event.preventDefault();
    }

    const uploadNewAd = (SlotNumber?: number) => {
        setPageText("Upload Ad");
        setSelectedAd(
            {
                Id: 0,
                SlotNumber: SlotNumber ? SlotNumber : sponsoredAds.length + 1,
                Name: "",
                AdUrl: "",
                ViewCount: null
            }
        )
        setSelectedAdFile(null);
        setShowDialog(true);

    }

    const editExistingAd = (row: ISponsoredAdsUpper) => {
        setPageText("Edit Ad");
        setSelectedAd(row.SponsoredAdInformation);
        setShowDialog(true);
        setSelectedAdFile(row.SponsoredAd);
    }

    const handleClose = () => {
        setShowDialog(false);
    }

    const handleSave = async (ad: ISponsoredAdsUpper) => {
        try {
            const formData = new FormData();
            if (ad) {
                formData.append('image', base64ToBlob(ad.SponsoredAd.FileContents));
                formData.append('Name', ad.SponsoredAdInformation.Name);
                formData.append('AdUrl', ad.SponsoredAdInformation.AdUrl);
                formData.append('SlotNumber', ad.SponsoredAdInformation.SlotNumber.toString());
            } else {
                console.error('selectedFile is null or undefined.');
            }
            await UploadSponsoredAd(formData);
            showSnackbar('Successfully Submitted!', 'success', 5000);
            handleClose();
            getAds();
        }
        catch (error: any) {
            showSnackbar(error, 'error', 5000);
        }
    }

    useEffect(() => {
        if (currentPage?.read) {
            getAds();
        }
    }, [currentPage])

    useEffect(() => {
        setDisabled(difference < 0);
    }, [startDateTime, endDateTime]);

    return (
        <div>
            <ThemeProvider theme={theme}>
                {currentPage?.read && <Grid className={classes.formContainer} alignItems={"center"} container>
                    <Grid item xs={12}>
                        <Grid
                            container
                            rowSpacing={4}
                            className={classes.subWrapper2}
                            direction={"column"}
                            justifyContent={'center'}
                            alignItems={"center"}
                        >
                            <Grid item xs={12}>
                                <Grid container justifyContent={"center"} alignItems={"center"} direction={"column"}>
                                    <Grid item xs={6}>
                                        <PageHeader title={"Manage Sponsored Ads"} onBackClick={undefined} />
                                    </Grid>
                                </Grid>
                                <Grid container direction={"row"} columnSpacing={4} alignItems={"center"} justifyContent={"center"} className={classes.gridPaddingTop}>
                                    <Grid item xs={12}>
                                        {isLoadingAds ?
                                            <Skeleton variant='rectangular' width={'780px'} height={'218px'} />
                                            :
                                            <Paper variant={"outlined"} square sx={{ backgroundColor: '#969696' }}>
                                                <List className={classes.adList}>
                                                    {sponsoredAds.map((ad) => (
                                                        <div
                                                            key={ad.SponsoredAdInformation.Id}
                                                            className={`${classes.hoverableItem} ${classes.hoverableItemHover} ${classes.listItemSpacing}`}
                                                        >
                                                            <ListItem sx={{ backgroundColor: '#ffffff' }}>
                                                                <div>
                                                                    <h4>{ad.SponsoredAdInformation.Name}</h4>
                                                                    <p>Ad URL: {ad.SponsoredAdInformation.AdUrl}</p>
                                                                    <img
                                                                        id={`ad-${ad.SponsoredAdInformation.Id}`}
                                                                        src={`data:${ad.SponsoredAd.ContentType};base64,${ad.SponsoredAd.FileContents}`}
                                                                        alt={`Ad ${ad.SponsoredAdInformation.Id}`}
                                                                        style={{ maxWidth: '728px', maxHeight: '90px' }}
                                                                    />
                                                                </div>
                                                            </ListItem>
                                                            <div className={classes.backdrop}>
                                                                <Tooltip title={'edit'} disableInteractive arrow>
                                                                    <Fab color='primary' onClick={(event) => editExistingAd(ad)} className={classes.FABSpacing}>
                                                                        <EditIcon />
                                                                    </Fab>
                                                                </Tooltip>
                                                                <Tooltip title={'replace'} disableInteractive arrow>
                                                                    <Fab color='primary' onClick={(event) => uploadNewAd(ad.SponsoredAdInformation.SlotNumber)} className={classes.FABSpacing}>
                                                                        <SwapHorizIcon />
                                                                    </Fab>
                                                                </Tooltip>
                                                            </div>
                                                        </div>
                                                    ))}
                                                    {Array.isArray(sponsoredAds) && sponsoredAds.length < MAX_NUMBER_OF_ADS &&
                                                        <ListItem >
                                                            <Grid container>
                                                                <Grid xs={12} item>
                                                                    <Paper
                                                                        variant='outlined'
                                                                        style={{
                                                                            border: '2px dashed #aaa'
                                                                        }}
                                                                    >
                                                                        <Grid container direction={"column"} alignItems={'center'} justifyContent={'center'} className={classes.itemPadding}>
                                                                            <Grid item>
                                                                                <p>Click to add a new ad to the list</p>
                                                                            </Grid>
                                                                            <Grid item>
                                                                                {/* Add icon and hover effect */}
                                                                                <Tooltip title={"add"} disableInteractive arrow>
                                                                                    <Fab color='primary' onClick={event => uploadNewAd(0)}>
                                                                                        <AddIcon />
                                                                                    </Fab>
                                                                                </Tooltip>
                                                                            </Grid>
                                                                        </Grid>
                                                                    </Paper>
                                                                </Grid>
                                                            </Grid>
                                                        </ListItem>
                                                    }
                                                </List>
                                            </Paper>}
                                    </Grid>

                                </Grid>
                                <Grid container justifyContent={'center'} alignItems={"center"} className={classes.gridPaddingTop} >
                                    <Grid item xs={7} alignSelf={"center"} className={classes.gridPaddingTop} >
                                        <Grid container direction={"column"} rowSpacing={3} justifyContent={'center'} alignItems={"center"}>
                                            <PageHeader title={"Views Report"} onBackClick={undefined} />
                                            <Grid item xs={12}>
                                                <Grid
                                                    container
                                                    direction={"row"}
                                                    justifyContent="center"
                                                    alignItems="center"
                                                    className={classes.gridPadding}
                                                    columnSpacing={4}
                                                >
                                                    <Grid item xs={6}>
                                                        <FormControl>
                                                            <Typography>View Count Start Date</Typography>
                                                            <LocalizationProvider dateAdapter={AdapterDayjs}>
                                                                <DatePicker
                                                                    defaultValue={dayjs(startDateTime)}
                                                                    disableFuture
                                                                    views={['year', 'month']}
                                                                    closeOnSelect
                                                                    onChange={handleStartDate}
                                                                />
                                                            </LocalizationProvider>
                                                        </FormControl>
                                                    </Grid>
                                                    <Grid item xs={6}>
                                                        <FormControl>
                                                            <Typography>View Count End Date</Typography>
                                                            <LocalizationProvider dateAdapter={AdapterDayjs}>
                                                                <DatePicker
                                                                    defaultValue={dayjs(endDateTime)}
                                                                    disableFuture
                                                                    views={['year', 'month']}
                                                                    closeOnSelect
                                                                    onChange={handleEndDate}
                                                                />
                                                            </LocalizationProvider>
                                                        </FormControl>
                                                    </Grid>
                                                </Grid>
                                            </Grid>
                                            <Grid item xs={4} >
                                                <Button onClick={handleSubmit} disabled={isDisabled} variant="contained" fullWidth>
                                                    Download
                                                </Button>
                                            </Grid>
                                        </Grid>
                                    </Grid>
                                </Grid>
                            </Grid>
                        </Grid>
                        {currentPage?.read && <Grid item sm={12}>
                            {showDialog && <SponsoredAdDialog
                                showDialog={showDialog}
                                selectedAd={selectedAd}
                                pageText={pageText}
                                handleClose={handleClose}
                                handleSave={handleSave}
                                adFile={selectedAdFile}
                            />}
                        </Grid>}
                        {isLoading &&
                            <LoadingDialog
                                loading={isLoading} />
                        }
                    </Grid>
                </Grid>
                }
                {!currentPage?.read && currentPage?.read !== undefined &&
                    <RedirectToDash />
                }
            </ThemeProvider>
        </div >
    );
}